import Drawer from "@mui/material/Drawer"
import Typography from "@mui/material/Typography"
import Divider from "@mui/material/Divider"
import CloseIcon from "@mui/icons-material/Close"
import {
  DrawerContainer,
  DrawerHeader,
  DrawerSection,
  DrawerMainContainer,
  DrawerCell,
  DrawerOpportunityCard,
  DrawerGoalCard,
  DrawerStatementCard,
  ChartWrapper,
} from "./Atom"
import { Box } from "@mui/material"
import { colors } from "../../../../styles/commonStyles"
import MixedChart from "../../Chart/MixedChart"
import { useEffect, useState } from "react"
import { chartPlottingDataType } from "./types"
import { useSelector } from "react-redux"
import {
  setIsProntoAiLoading,
  setProntoAiData,
  setProntoAiStoreData,
  setSelectedMetric,
} from "../../../redux/slices/prontoAiSlice"
import { useDispatch } from "react-redux"
import { dynamicDatasets } from "./config"
import ProntoAiLoader from "../../Loader/ProntoAiLoader"
import { convertToInternationalNumberSystem } from "../../../../helper/utils/formatting"

export default function ProntoAiDrawer({ state, closeSidebar }: any) {
  const dispatch = useDispatch()

  const handleSidebarClose = () => {
    dispatch(setProntoAiData([]))
    dispatch(setProntoAiStoreData([]))
    dispatch(setSelectedMetric(null))
    dispatch(setIsProntoAiLoading(false))
    closeSidebar()
  }

  const data = useSelector((state: any) => state.prontoAi?.prontoAiData)
  const selectedMetric = useSelector(
    (state: any) => state.prontoAi?.selectedMetric,
  )
  const isProntoAiLoading = useSelector(
    (state: any) => state.prontoAi?.isProntoAiLoading,
  )

  const isAR = data?.store_data?.[0]?.column_id?.includes("current")

  const opportunityData = Object?.keys?.(data?.opportunity_data || {})?.length
    ? [
        {
          opportunity_id: "opportunity_percent_difference",
          opportunity_name: "Opportunity % Change",
          opportunity_value: data?.opportunity_data
            ?.opportunity_percent_difference
            ? convertToInternationalNumberSystem(
                data?.opportunity_data?.opportunity_percent_difference,
                "%",
              )
            : null,
        },
        {
          opportunity_id: "opportunity_duration",
          opportunity_name: "Opportunity Duration",
          opportunity_value: data?.opportunity_data?.opportunity_duration
            ? `${data?.opportunity_data?.opportunity_duration} Days`
            : null,
        },
        {
          opportunity_id: "percent_difference_needed",
          opportunity_name: "% Difference Needed",
          opportunity_value: data?.opportunity_data?.percent_difference_needed
            ? convertToInternationalNumberSystem(
                data?.opportunity_data?.percent_difference_needed,
                "%",
              )
            : null,
        },
      ]
    : null
  const goalData = [
    {
      column_id: "last_month_actual",
      column_name: "Last Month Actual",
      column_value: data?.goal_data?.last_month_actual,
      column_datatype: data?.goal_data?.last_month_actual_data_type,
    },
    {
      column_id: "last_month_goal",
      column_name: "Last Month Goal",
      column_value: data?.goal_data?.last_month_goal,
      column_datatype: data?.goal_data?.last_month_goal_data_type,
    },
    {
      column_id: "benchmark_goal",
      column_name: "Benchmark",
      column_value: data?.goal_data?.benchmark_goal,
      column_datatype: data?.goal_data?.benchmark_goal_data_type,
    },
    {
      column_id: "previous_month_goal_impact",
      column_name: "Previous Month Goal Impact",
      column_value: data?.goal_data?.previous_month_goal_impact,
      column_datatype: "$",
    },
    {
      column_id: "benchmark_impact",
      column_name: "Benchmark Impact",
      column_value: data?.goal_data?.benchmark_impact,
      column_datatype: "$",
    },
  ]

  const [chartPlottingData, setChartPlottingData] = useState<
    chartPlottingDataType[]
  >([])

  useEffect(() => {
    setChartPlottingData([])
    const chartsData: any[] = data?.chart_data
      ? Object?.values(data?.chart_data)
      : []
    chartsData.forEach((chart) => {
      setChartPlottingData((prev: any) => [
        ...prev,
        dynamicDatasets(chart, chartsData?.length),
      ])
    })
  }, [data])

  useEffect(() => {
    setTimeout(() => {
      dispatch(setIsProntoAiLoading(false))
    }, 4000)
  }, [data])

  return (
    <>
      <Drawer
        anchor={"right"}
        open={state["right"]}
        variant="persistent"
        onClose={() => handleSidebarClose()}
      >
        <DrawerContainer>
          <DrawerHeader>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography fontWeight={700} fontSize={16}>
                {selectedMetric?.Metric}
              </Typography>
              <Typography
                sx={{ color: colors.primaryGrey }}
                fontSize={12}
                fontWeight={500}
              >
                {selectedMetric?.practice_name}
              </Typography>
            </Box>
            <CloseIcon
              sx={{ cursor: "pointer" }}
              onClick={() => handleSidebarClose()}
            />
          </DrawerHeader>

          <Divider sx={{ margin: "0 -21px" }} />

          {!isProntoAiLoading ? (
            <DrawerMainContainer>
              <DrawerSection>
                {data?.store_data?.map((cell: any, index: any) => {
                  if (index < 4) {
                    return (
                      <DrawerCell type="1" key={cell?.column_id} cell={cell} />
                    )
                  }
                  if (isAR) {
                    return (
                      <DrawerCell type="1" key={cell?.column_id} cell={cell} />
                    )
                  }
                  return (
                    <DrawerCell type="2" key={cell?.column_id} cell={cell} />
                  )
                })}
              </DrawerSection>
              {opportunityData && (
                <DrawerSection
                  sx={{
                    padding: "20px",
                    border: "none",
                    borderTop: "1px solid #E4E4E4",
                  }}
                >
                  {opportunityData.map((op: any) => (
                    <DrawerOpportunityCard key={op.opportunity_id} op={op} />
                  ))}
                </DrawerSection>
              )}

              <DrawerSection
                sx={{
                  padding: opportunityData ? "0 20px 20px 20px" : "20px",
                  border: "none",
                }}
              >
                {chartPlottingData.map(
                  (chart: chartPlottingDataType, index: number | undefined) => (
                    <ChartWrapper key={index}>
                      <MixedChart
                        chartName={chart.chartName}
                        chartData={chart.data}
                        scales={chart.scales}
                      />
                    </ChartWrapper>
                  ),
                )}
              </DrawerSection>
              <DrawerSection sx={{ padding: "0px 20px", border: "none" }}>
                {goalData.map((goal: any) => (
                  <DrawerGoalCard key={goal?.column_id} goal={goal} />
                ))}
              </DrawerSection>
              <DrawerSection sx={{ padding: "20px", border: "none" }}>
                <DrawerStatementCard statement={data?.impact_statement} />
              </DrawerSection>
            </DrawerMainContainer>
          ) : (
            <ProntoAiLoader />
          )}
        </DrawerContainer>
      </Drawer>
    </>
  )
}
